import {
  Autocomplete,
  Checkbox,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  styled,
  TextField,
  Tooltip,
} from "@mui/material";
import CiscoAvatar from "components/app/CiscoAvatar";
import { AssigneeOption } from "components/todolist/EditTaskDialog/Assignee";
import { ChangeEvent, FC, Fragment, useMemo, useRef, useState } from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form/dist/types/controller";
import { useAssigneeList, useTeamAssigneeList } from "../hooks";

const StyledChip = styled(Chip)({
  fontSize: "0.2rem",
  margin: 2,
});

const GroupedAssignee: FC<{
  field: ControllerRenderProps;
  fieldState: ControllerFieldState;
}> = ({ fieldState, field }) => {
  const [keyword, setKeyword] = useState("");
  const [inputValue, setInputValue] = useState("");
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { data: assigneeList, mutate, isValidating } = useAssigneeList(keyword);
  const { data: teamList } = useTeamAssigneeList();
  const tags = 1;

  const groupedAssignees: (AssigneeOption & { group: string })[] = useMemo(() => {
    const gtl: (AssigneeOption & { group: string })[] = (teamList || [])
      .map((t: AssigneeOption) => ({
        ...t,
        group: "My Team",
      }))
      .filter((t: AssigneeOption & { group: string }) =>
        keyword === "" ? true : t.name.toLowerCase().includes(keyword.toLowerCase())
      );
    const gol = (assigneeList || [])
      .filter(
        (t: AssigneeOption) =>
          (teamList || []).map((o: AssigneeOption) => o.cecId).includes(t.cecId) === false
      )
      .map((t: AssigneeOption) => ({
        ...t,
        group: "Others",
      }));

    return [...gtl, ...gol];
  }, [assigneeList, teamList, keyword]);

  const [assignees, setAssignees] = useState<(AssigneeOption & { group: string })[]>(
    field.value ? field.value : []
  );

  const [isMyTeamSelected, setIsMyTeamSelected] = useState<boolean>(
    field.value.length === (teamList?.length || 0)
  );
  const indeterminate = useMemo(() => {
    if (!teamList) {
      return false;
    }
    const teamTmp = assignees.filter((a) => a.group === "My Team");

    return teamTmp.length > 0 && teamTmp.length !== teamList.length;
  }, [assignees, teamList]);

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    // only when user input str >=3 to call && trigger query within 300ms
    const val = e.target.value.trim();
    setInputValue(val);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      if (val.length >= 3) {
        setKeyword(val);
        mutate();
      } else {
        setKeyword("");
      }
    }, 300);
  };

  const handleTagRemove = (cecId: string) => {
    const tmp = assignees.filter((p) => p.cecId !== cecId);
    field.onChange(tmp);
    setAssignees(tmp);
  };

  const handleMyTeamSelect = (checked: boolean) => {
    setIsMyTeamSelected(checked);
    if (checked) {
      const myTeamAssignees = groupedAssignees.filter((a) => a.group === "My Team");
      setAssignees(myTeamAssignees);
      field.onChange(myTeamAssignees);
    } else {
      const filteredList = assignees.filter((a) => a.group !== "My Team");
      setAssignees(filteredList);
      field.onChange(filteredList);
    }
  };

  const handleChange = (value: (AssigneeOption & { group: string })[]) => {
    field.onChange(value);
    setAssignees(value);
    setIsMyTeamSelected(value.filter((v) => v.group === "My Team").length === teamList.length);
  };

  return (
    <div>
      <p
        style={{
          fontSize: 12,
        }}
      >
        Assignee To
      </p>

      <Autocomplete
        {...field}
        autoFocus
        multiple
        disableCloseOnSelect
        limitTags={tags}
        onClose={() => setKeyword("")}
        getOptionLabel={(option) => option?.name ?? ""}
        loading={isValidating}
        noOptionsText={"No assignee found"}
        isOptionEqualToValue={(o, v) => o.cecId === v.cecId}
        options={groupedAssignees || []}
        groupBy={(o) => o.group}
        filterOptions={(x) => x}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.cecId}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flex: 1,
              }}
            >
              <Checkbox
                checked={field.value?.some((v: AssigneeOption) => v.cecId === option.cecId)}
              />
              <CiscoAvatar
                username={option.cecId}
                style={{ width: 24, height: 24, marginRight: 12 }}
              />
              <div>
                <p style={{ lineHeight: 1.2 }}>{option.name}</p>
                <p style={{ color: "#777777", lineHeight: 1.2 }}>{option.role}</p>
              </div>
            </div>
          </li>
        )}
        renderGroup={(params) => (
          <Fragment key={params.group}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                height: "48px",
                fontSize: 12,
                fontWeight: "bold",
              }}
            >
              <Checkbox
                checked={isMyTeamSelected}
                indeterminate={indeterminate}
                onChange={(e) => handleMyTeamSelect(e.target.checked)}
                size="small"
                disableFocusRipple
                disableRipple
                disableTouchRipple
                style={{
                  visibility: params.group === "My Team" ? "visible" : "hidden",
                }}
              />
              {params.group}
            </div>
            {params.children}
          </Fragment>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            margin="normal"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              type: "search",
              style: {
                height: 40,
                borderRadius: 8,
              },
            }}
            sx={{ marginTop: 0 }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            size={"small"}
            placeholder={assignees.length === 0 ? "Please select or search" : ""}
            value={inputValue}
            onChange={handleInput}
          />
        )}
        value={assignees}
        onChange={(_e: any, value) => handleChange(value)}
        renderTags={(value, getTagProps) => (
          <div>
            <StyledChip
              label={value?.[0].name}
              {...getTagProps({ index: 0 })}
              size={"small"}
            />
            {value.length > tags ? (
              <Tooltip
                title={
                  <List
                    disablePadding={true}
                    dense={true}
                  >
                    {value.slice(tags).map((x) => (
                      <ListItem
                        key={x.cecId}
                        style={{
                          width: "240px",
                        }}
                      >
                        <ListItemText primary={x.name} />
                        <ListItemSecondaryAction>
                          <ListItemButton
                            onClick={() => handleTagRemove(x.cecId)}
                            disableRipple
                          >
                            x
                          </ListItemButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                }
              >
                <Chip
                  size={"small"}
                  label={`${value.length - tags} more`}
                />
              </Tooltip>
            ) : null}
          </div>
        )}
      />
    </div>
  );
};

export default GroupedAssignee;
